import React from "react";
import { graphql, useStaticQuery } from "gatsby";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import useSiteMetadata from "@hooks/use-site-metadata";
import tw from "twin.macro";

import Button from "@components/Button";
import Callout from "@components/Callout";
import Container from "@components/Container";
import { headingTwo, boldHeader, boldHeaderSup } from "@styles/globalStyles";
import Layout from "@components/Layout";
import OneTrust from "@components/OneTrust";
import Seo from "@components/Seo";
import GlobalHead from "@components/GlobalHead";

// Assets
import iconBroadSpectrum from "@images/home/icon-broad-spectrum.svg";
import iconFiveYears from "@images/home/icon-five-years.svg";
import iconProven from "@images/home/icon-proven.svg";
import patternWhy from "@images/home/pattern-why.svg";
import patternEfficacy from "@images/home/pattern-efficacy.svg";

const IndexPage = ({ location }) => {
  const data = useStaticQuery(graphql`
    query {
      efficacy: file(relativePath: { eq: "home/efficacy.png" }) {
        childImageSharp {
          gatsbyImageData(
            width: 656
            quality: 80
            placeholder: BLURRED
            formats: [AUTO, WEBP, AVIF]
          )
        }
      }
      hero: file(relativePath: { eq: "home/hero.png" }) {
        childImageSharp {
          gatsbyImageData(
            width: 563
            quality: 80
            placeholder: BLURRED
            formats: [AUTO, WEBP, AVIF]
          )
        }
      }
    }
  `);

  return (
    <Layout pageReferences="homepage" location={location}>
      {/* hero */}
      <section tw="relative grid max-w-[100vw] overflow-clip z-0 lg:(overflow-y-visible)">
        {/* background image - centered on large screens so there is no shift. */}
        <div tw="relative [grid-area: 1/1] mx-auto lg:(absolute w-[1224px] max-w-[1224px] left-[50%] translate-x-[-50%] h-full)">
          <div
            className="mainGradient"
            tw="bg-heroGradient h-full min-h-[18rem] w-screen relative lg:(min-h-[20.25rem] left-1/2 -translate-x-1/2)"
          ></div>
        </div>
        <div tw="[grid-area: 1/1] max-w-[100vw] z-[1] flex min-h-full lg:(min-h-[20.25rem] items-center)">
          <Container tw="lg:(flex flex-row-reverse)">
            <div tw="mx-auto -mt-[5.9rem] w-fit lg:(-mr-50 -mt-28)">
              <GatsbyImage
                image={getImage(data.hero)}
                alt=""
                imgStyle={{ objectPosition: "top" }}
                tw="mx-[1.3125rem] lg:(mx-0 w-[35.1875rem])"
              />
            </div>
            <div tw="lg:(pt-[4.88rem] pb-[4.2rem])">
              <h1
                css={[
                  tw`text-grey font-medium uppercase text-3xl tracking-[0.225rem] leading-[1.1] lg:(text-[2.5rem] leading-[1.1] tracking-[0.3rem] -mr-10)`,
                ]}
              >
                Broad-spectrum coverage for a broad range of{" "}
                <span tw="lowercase">c</span>IAI patients
                <sup>1</sup>
              </h1>
              <p tw="text-periwinkle01 font-bold text-xl tracking-[0.025rem] leading-[1.1] mt-4 lg:(text-2xl leading-[1.055] tracking-[0.03rem] pr-10 mt-5)">
                Empirically treat complicated intra-abdominal infection (cIAI)
                in&nbsp;adults with XERAVA
              </p>
              <Button
                internal
                to="/why"
                tw="inline-block mt-30 mb-[2.625rem] bg-periwinkle01 text-deepViolet01 font-black w-fit hocus:(bg-periwinkle00) active:(bg-magenta text-grey) lg:(mb-0 mt-[2.375rem])"
              >
                Explore XERAVA
              </Button>
            </div>
          </Container>
        </div>
      </section>
      <section
        id="why-xerava"
        tw="relative z-[1] -mt-4 lg:(pb-50 scroll-mt-32)"
      >
        <Container tw="relative bg-white py-50 px-[0.8rem] shadow-[2px 2px 4px 3px rgba(27, 18, 50, 0.10)] lg:(px-50)">
          <img
            src={patternWhy}
            alt=""
            tw="absolute z-[-1] -bottom-[61px] -left-[198px] w-[405px] max-w-none lg:(-bottom-[120px] -left-[280px] w-[580px])"
          />
          <h2 css={[headingTwo]}>Why XERAVA</h2>
          <p css={[boldHeader, tw`mt-5`]}>
            Powerful empiric treatment for <br />
            more of your <span tw="lowercase">c</span>IAI&nbsp;patients
            <sup css={[boldHeaderSup]}> 2</sup>
          </p>
          <div tw="text-center">
            <div tw="lg:(flex justify-between)">
              <div tw="mt-10 lg:(mt-10)">
                <img src={iconBroadSpectrum} alt="" tw="mx-auto" />
                <p tw="mt-5 lg:(mt-30 w-[14.6875rem])">
                  Broad-spectrum activity that covers Gram-positive,
                  Gram-negative, and anaerobic bacteria, including
                  multidrug-resistant strains<sup>1</sup>
                </p>
              </div>
              <div tw="mt-[2.03rem] lg:(mt-10)">
                <img src={iconProven} alt="" tw="mx-auto" />
                <p tw="mt-5 lg:(mt-30 w-52)">
                  Proven clinically effective vs. carbapenem therapy; effective
                  and safe as a monotherapy<sup>3</sup>
                </p>
              </div>
              <div tw="mt-[2.03rem] lg:(mt-10)">
                <img src={iconFiveYears} alt="" tw="mx-auto" />
                <p tw="mt-5 lg:(mt-30 w-64)">
                  Optimal dosing with 5+ years <br tw="hidden lg:(block)" />
                  of real&#8209;world experience and requires no renal dose
                  adjustments or therapeutic drug monitoring<sup>4</sup>
                </p>
              </div>
            </div>
            <Button
              internal
              to="/why"
              tw="inline-block font-black mt-10 lg:(w-fit mt-7)"
            >
              Discover the difference
            </Button>
          </div>
        </Container>
      </section>
      <section
        id="efficacy"
        tw="py-50 overflow-x-clip scroll-mt-12 lg:(pt-50 pb-100 scroll-mt-40)"
      >
        <Container tw="relative lg:(flex flex-row-reverse gap-5)">
          <img
            src={patternEfficacy}
            alt=""
            tw="absolute z-[-1] -bottom-[50px] -right-[93px] w-[210px] lg:(-right-[139px] w-[269px])"
          />
          <div>
            <h2 css={[headingTwo, tw`text-left`]}>Efficacy</h2>
            <p css={[boldHeader, tw`mt-5 text-left`]}>
              Confidently <br tw="lg:(hidden)" />
              take on <span tw="lowercase">c</span>IAI
            </p>
            <p tw="mt-5">
              In 2 rigorous phase 3, randomized, double-blind, double-dummy,
              multicenter, prospective trials comparing&nbsp;XERAVA with either
              ertapenem or meropenem for patients with cIAI, XERAVA demonstrated
              <sup>3,5</sup>:
            </p>
            <ul tw="mt-30 border-l-[0.34rem] border-periwinkle01 pl-[1.375rem]">
              <li>
                Robust efficacy as monotherapy<sup>3,5,6</sup>
              </li>
              <li tw="py-[1.0625rem]">High clinical cure rates </li>
              <li>Success against resistant strains</li>
            </ul>
            <Button
              internal
              to="/efficacy-and-safety"
              tw="block font-black mt-6 w-fit px-[3.82rem] mx-auto lg:(mt-10 mx-0)"
            >
              Explore trial data
            </Button>
          </div>
          <div tw="mx-auto mt-10 max-w-[41rem] lg:(mt-0)">
            <GatsbyImage
              image={getImage(data.efficacy)}
              alt=""
              imgStyle={{ objectPosition: "top" }}
              tw="-mx-2.5 lg:(mx-0 w-[23.3125rem])"
            />
          </div>
        </Container>
      </section>
      <section
        id="safety"
        tw="bg-periwinkle50 pt-50 pb-[3.75rem] lg:(pt-100 scroll-mt-32)"
      >
        <Container>
          <h2 css={[headingTwo]}>Safety</h2>
          <p css={[boldHeader, tw`mt-5 px-10`]}>
            Well tolerated in 2 pivotal <br tw="hidden lg:(block)" />
            clinical trials
          </p>
          <p tw="mt-5 text-center px-2 lg:(px-0)">
            Safety was evaluated in 2 pivotal trials that included a broad range
            of patients.
          </p>
          <Button
            internal
            to="/efficacy-and-safety#safety"
            tw="block font-black mt-10 w-fit pl-[1.2rem] pr-[2.45rem] mx-auto"
          >
            Learn more about safety
          </Button>
        </Container>
      </section>
      <Callout tw="pt-50 pb-10" refNumber="1" />
    </Layout>
  );
};

export default IndexPage;

export const Head = ({ location }) => {
  const siteMetadata = useSiteMetadata();

  return (
    <>
      <GlobalHead />
      <Seo
        title={siteMetadata.title}
        description={siteMetadata.description}
        image={siteMetadata.image}
        siteUrl={siteMetadata.siteUrl}
        pathname={location?.pathname}
        useGTM={true}
      >
        <script key="schema" type="application/ld+json">
          {`
          {
            "@context": "http://schema.org",
            "@type": "WebSite",
            name: " XERAVA® (eravacycline)",
            url: "https://www.xerava.com/",
          }
        `}
        </script>
      </Seo>
      {process.env.GATSBY_ONETRUST_TOKEN && (
        <OneTrust otToken={process.env.GATSBY_ONETRUST_TOKEN} />
      )}
    </>
  );
};
